import {faEyeSlash, faPlayCircle, faRotateLeft} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Text} from '@rdcomponents/text/text';
import {Button, Popover, Tag} from 'antd';
import {useMemo, useState} from 'react';

import {appRoute} from '../../../../../app-route';
import {NavigationLink} from '../../../../../layout/navigation-link/navigation-link';
import {Locale} from '../../../../../provider/locale/locale';
import {LocalePlural} from '../../../../../provider/locale/locale-plural';
import {LocalePluralLangKeyType} from '../../../../../provider/locale/translation/type';
import {useSnackbar} from '../../../../../provider/snackbar/snackbar-hook';
import {DashboardPostponeRecommendationPayloadApiType} from '../../../../../service/dashboard/dashboard-recommendations/dashboard-recommendations-api-type';
import {useDashboardRecommendation} from '../../../../../service/dashboard/dashboard-recommendations/dashboard-recommendations-hook';
import {DashboardRecommendationTypeEnum} from '../../../../../service/dashboard/dashboard-recommendations/dashboard-recommendations-type';
import {classNames} from '../../../../../util/css';
import {useFormat} from '../../../../../util/format-hook/format-hook';
import {ProvidersIdsEnum} from '../../../../../util/type';
import {appendQueryParametersToUrl, objectToUrlParameters} from '../../../../../util/url';
import {useKnowledgeVideos} from '../../../knowledge/knowledge-help-videos/use-knowledge-videos';

import {recommendationLinkText} from './dashboard-recommendation-card-const';
import {DashboardRecommendationsPostponeForm} from './dashboard-recommendation-postpone-form/dashboard-recommendation-postpone-form';
import * as styles from './dashboard-recommendation-card.scss';

type PropsType = {
    count: number;
    recommendationKey: DashboardRecommendationTypeEnum;
    postponedDate?: Date | null;
    filterKey?: string;
    startDate?: string;
    endDate?: string;
    onPostponeChange: (recommendationKey: DashboardRecommendationTypeEnum, postpone: number | null) => void;
    showBottomBorder?: boolean;
    onLinkClick: () => void;
    path: string;
    catalogId?: ProvidersIdsEnum;

    title: React.ReactNode;
    subtitle: React.ReactNode;
    details: React.ReactNode;

    tagCounterPluralLangKeys: LocalePluralLangKeyType;
};

export function DashboardRecommendation(props: PropsType): JSX.Element {
    const {
        recommendationKey,
        postponedDate,
        onPostponeChange,
        count,
        filterKey,
        startDate,
        endDate,
        showBottomBorder,
        onLinkClick,
        path,
        catalogId,
        title,
        subtitle,
        details,
        tagCounterPluralLangKeys,
    } = props;

    const {getFormattedNumber} = useFormat();
    const {snackbar} = useSnackbar();
    const {cancelRecommendationPostpone, postponeRecommendation, isInProgress} = useDashboardRecommendation();

    const pathLink = useMemo(() => {
        const parameters: Record<string, string | boolean | undefined> = filterKey
            ? {'filter-id': filterKey}
            : startDate
            ? {
                  'date-from': startDate,
                  'date-to': endDate,
                  'replies-to-reviews': 'false',
                  'ordering': '-creation_data',
                  'possibility-of-an-answer': true,
              }
            : {};

        if (catalogId) {
            parameters.catalogs_ids = catalogId.toString();
        }

        return appendQueryParametersToUrl(path, parameters);
    }, [catalogId, endDate, filterKey, path, startDate]);

    const {dashboardRecommendationLinkExists} = useKnowledgeVideos();

    const hasHelpVideoLink = dashboardRecommendationLinkExists(recommendationKey);

    function processCancelPostpone() {
        cancelRecommendationPostpone(recommendationKey)
            .then(() => {
                onPostponeChange(recommendationKey, null);
            })
            .catch(() => {
                snackbar.error(
                    <Locale stringKey="DASHBOARD_PAGE__RECOMMENDATION__POSTPONE_OPTION__ERROR_CANCELING_POSTPONE" />
                );
            });
    }

    function processPostpone(payload: DashboardPostponeRecommendationPayloadApiType) {
        postponeRecommendation(recommendationKey, payload)
            .then((response) => {
                onPostponeChange(recommendationKey, response.postponed);
            })
            .catch(() => {
                snackbar.error(<Locale stringKey="DASHBOARD_PAGE__RECOMMENDATION__POSTPONE_OPTION__ERROR_POSTPONE" />);
            });
    }

    const {getFormattedDateTime} = useFormat();
    const [isOpen, setIsOpen] = useState<boolean>(false);

    return (
        <div
            className={classNames(styles.DashboardRecommendation, {
                [styles.DashboardRecommendation__borderedBottom]: showBottomBorder,
            })}
        >
            <div className={styles.DashboardRecommendation_header}>
                <div>
                    <div className={styles.DashboardRecommendation_titleWrapper}>
                        <p className={styles.DashboardRecommendation_title}>{title}</p>
                        <Text block secondary small>
                            {subtitle}
                        </Text>
                    </div>
                    <Text block lighter>
                        {details}
                    </Text>
                </div>

                {postponedDate ? (
                    <Button
                        className={styles.DashboardRecommendation_actionButton}
                        icon={<FontAwesomeIcon icon={faRotateLeft} />}
                        loading={isInProgress}
                        onClick={processCancelPostpone}
                        size="middle"
                        type="text"
                    />
                ) : (
                    <Popover
                        content={
                            <DashboardRecommendationsPostponeForm
                                isInProgress={isInProgress}
                                postponeCard={processPostpone}
                                setIsOpen={setIsOpen}
                            />
                        }
                        open={isOpen}
                        placement="bottomRight"
                        title={<Locale stringKey="DASHBOARD_PAGE__RECOMMENDATION__POSTPONE_TITLE" />}
                        trigger="click"
                    >
                        <Button
                            className={styles.DashboardRecommendation_actionButton}
                            icon={<FontAwesomeIcon icon={faEyeSlash} />}
                            onClick={() => {
                                setIsOpen(!isOpen);
                            }}
                            size="middle"
                            type="text"
                        />
                    </Popover>
                )}
            </div>

            <div className={styles.DashboardRecommendation_actionWrapper}>
                <Tag
                    className={classNames(styles.DashboardRecommendation_counter, {
                        [styles.DashboardRecommendation_counter__disabled]: Boolean(postponedDate),
                    })}
                >
                    <LocalePlural
                        count={count}
                        fewKey={tagCounterPluralLangKeys.few}
                        manyKey={tagCounterPluralLangKeys.many}
                        singularKey={tagCounterPluralLangKeys.singular}
                        valueMap={{
                            count: getFormattedNumber(count),
                        }}
                    />
                </Tag>
                {postponedDate ? (
                    <span className={styles.DashboardRecommendation_postponedDate}>
                        <Locale
                            stringKey="DASHBOARD_PAGE__RECOMMENDATION__POSTPONED_TO"
                            valueMap={{
                                date: getFormattedDateTime(postponedDate),
                            }}
                        />
                    </span>
                ) : (
                    <div className={styles.DashboardRecommendation_linksWrapper}>
                        {hasHelpVideoLink && (
                            <NavigationLink
                                className={styles.DashboardRecommendation_linkButton}
                                onClick={onLinkClick}
                                to={`${appRoute.knowledgeHelpVideos.path}?${objectToUrlParameters({
                                    recommendationKey,
                                })}`}
                            >
                                <FontAwesomeIcon icon={faPlayCircle} />
                            </NavigationLink>
                        )}
                        <NavigationLink
                            className={styles.DashboardRecommendation_linkButton}
                            isTargetBlank={Boolean(filterKey)}
                            onClick={onLinkClick}
                            to={pathLink}
                        >
                            <Locale stringKey={recommendationLinkText[recommendationKey]} />
                        </NavigationLink>
                    </div>
                )}
            </div>
        </div>
    );
}
